import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { EventAvailable } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import {
  cancelVenueBooking,
  createVenueBooking,
  fetchVenueSlots,
  createSharedVenueBooking,
  fetchSharedVenueSlots,
  rescheduleVenueBooking,
} from "./api";
import BankTransferPaymentDialog, {
  type BankTransferPaymentDetails,
} from "./BankTransferPaymentDialog";
import CancelVenueBookingDialog from "./CancelVenueBookingDialog";
import ConfirmVenueBookingDialog, {
  type ConfirmVenueBookingPayload,
} from "./ConfirmVenueBookingDialog";
import ConfirmVenueRescheduleDialog from "./ConfirmVenueRescheduleDialog";
import MyVenueBookings from "./MyVenueBookings";
import type { VenueBooking, VenueHireSettings, VenueSlot } from "./types";
import VenueHireDateCalendar from "./VenueHireDateCalendar";
import {
  filterSlotsByLength,
  firstDateWithAvailableSlots,
  formatDate,
  formatSlotRange,
  formatTime,
  formatVenueBookingPrice,
  getRollingDateRange,
  getVenueBookingSlotLength,
  isSlotTakenError,
  isInvalidVoucherError,
  isValidVenueBooking,
  isVenueBookingInPast,
  preferredSlotLengthFilter,
  slotLengthOf,
  summariseSlotsByDate,
  toDateString,
  venueSlotKey,
  type SlotLengthFilter,
  venueHirePaymentDeadlineNotice,
} from "./venueHireUtils";

const SLOT_LENGTH_TABS: { value: SlotLengthFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: 30, label: "30 min" },
  { value: 60, label: "60 min" },
  { value: 90, label: "90 min" },
];

interface VenueHireBookingProps {
  clubId: number;
  clubName: string;
  venueHire?: VenueHireSettings;
  shareToken?: string;
  requiresName?: boolean;
}

const VenueHireBooking: React.FC<VenueHireBookingProps> = ({
  clubId,
  clubName,
  venueHire,
  shareToken,
  requiresName = false,
}) => {
  const [slots, setSlots] = useState<VenueSlot[]>([]);
  const [rangeEnd, setRangeEnd] = useState<Dayjs>(() =>
    dayjs(getRollingDateRange(4).to),
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingSlotKey, setBookingSlotKey] = useState<string | null>(null);
  const [slotToConfirm, setSlotToConfirm] = useState<VenueSlot | null>(null);
  const [paymentDetails, setPaymentDetails] =
    useState<BankTransferPaymentDetails | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [slotLengthFilter, setSlotLengthFilter] =
    useState<SlotLengthFilter>("all");
  const [bookingsRefreshKey, setBookingsRefreshKey] = useState(0);
  const [reschedulingBooking, setReschedulingBooking] =
    useState<VenueBooking | null>(null);
  const [slotToReschedule, setSlotToReschedule] = useState<VenueSlot | null>(
    null,
  );
  const [bookingToCancel, setBookingToCancel] = useState<VenueBooking | null>(
    null,
  );
  const [cancellingBookingId, setCancellingBookingId] = useState<number | null>(
    null,
  );
  const hasSetInitialFilter = useRef(false);

  useEffect(() => {
    hasSetInitialFilter.current = false;
  }, [clubId, shareToken]);

  const loadSlots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const range = getRollingDateRange(4);
      setRangeEnd(dayjs(range.to));
      const data = shareToken
        ? await fetchSharedVenueSlots(shareToken, range.from, range.to)
        : await fetchVenueSlots(clubId, range.from, range.to);
      setSlots(data);
    } catch (err) {
      console.error("Error loading venue slots:", err);
      setError("Failed to load available slots. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [clubId, shareToken]);

  useEffect(() => {
    if (venueHire?.enabled) {
      loadSlots();
    }
  }, [venueHire?.enabled, loadSlots]);

  const refreshBookingsAndSlots = useCallback(async () => {
    setBookingsRefreshKey((key) => key + 1);
    await loadSlots();
  }, [loadSlots]);

  const rearrangeSlots = useMemo(() => {
    if (!reschedulingBooking || !isValidVenueBooking(reschedulingBooking)) {
      return slots;
    }
    const targetPrice = reschedulingBooking.price;
    return slots.filter(
      (slot) =>
        slot.status === "available" &&
        slot.price === targetPrice &&
        !isVenueBookingInPast(slot),
    );
  }, [slots, reschedulingBooking]);

  const isRearranging =
    reschedulingBooking != null &&
    isValidVenueBooking(reschedulingBooking) &&
    !isVenueBookingInPast(reschedulingBooking);

  const displaySlots = isRearranging ? rearrangeSlots : slots;

  useEffect(() => {
    if (loading || hasSetInitialFilter.current || displaySlots.length === 0) {
      return;
    }
    hasSetInitialFilter.current = true;
    setSlotLengthFilter(preferredSlotLengthFilter(displaySlots));
  }, [loading, displaySlots]);

  useEffect(() => {
    if (!reschedulingBooking || !isValidVenueBooking(reschedulingBooking)) {
      return;
    }
    setSlotLengthFilter(getVenueBookingSlotLength(reschedulingBooking));
  }, [reschedulingBooking]);

  const filteredSlots = useMemo(
    () => filterSlotsByLength(displaySlots, slotLengthFilter),
    [displaySlots, slotLengthFilter],
  );

  const slotSummaries = useMemo(
    () => summariseSlotsByDate(filteredSlots),
    [filteredSlots],
  );

  const bookableHighlightDates = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const [date, summary] of Object.entries(slotSummaries)) {
      if (summary.hasAvailable) {
        map[date] = true;
      }
    }
    return map;
  }, [slotSummaries]);

  useEffect(() => {
    if (loading) return;
    if (filteredSlots.length === 0) {
      setSelectedDate(null);
      return;
    }
    setSelectedDate((current) => {
      if (current && slotSummaries[toDateString(current)]?.hasSlots) {
        return current;
      }
      const next = firstDateWithAvailableSlots(filteredSlots);
      return next ? dayjs(next) : null;
    });
  }, [filteredSlots, loading, slotSummaries]);

  useEffect(() => {
    if (
      reschedulingBooking &&
      (!isValidVenueBooking(reschedulingBooking) ||
        isVenueBookingInPast(reschedulingBooking))
    ) {
      setReschedulingBooking(null);
      setSlotToReschedule(null);
    }
  }, [reschedulingBooking]);

  useEffect(() => {
    if (bookingToCancel && !isValidVenueBooking(bookingToCancel)) {
      setBookingToCancel(null);
    }
  }, [bookingToCancel]);

  if (!venueHire?.enabled) {
    return null;
  }

  const slotKey = venueSlotKey;

  const handleBookClick = (slot: VenueSlot) => {
    setError(null);
    setVoucherError(null);
    if (reschedulingBooking && isValidVenueBooking(reschedulingBooking)) {
      setSlotToReschedule(slot);
      return;
    }
    setSlotToConfirm(slot);
  };

  const handleConfirmBooking = async (payload?: ConfirmVenueBookingPayload) => {
    if (!slotToConfirm) return;
    const slot = slotToConfirm;
    const key = slotKey(slot);
    try {
      setBookingSlotKey(key);
      setError(null);
      setVoucherError(null);
      const slotLength = slotLengthOf(slot);
      const bookingRequest = {
        date: slot.date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        slot_length: slotLength,
        voucher_code: payload?.voucher_code,
      };
      const result = shareToken
        ? await createSharedVenueBooking(shareToken, {
            ...bookingRequest,
            first_name: payload?.first_name,
            last_name: payload?.last_name,
          })
        : await createVenueBooking(clubId, bookingRequest);
      setSlotToConfirm(null);
      const isFree = result.free === true || result.booking.price === 0;
      if (isFree) {
        setSuccessMessage("Booking confirmed — no payment required.");
      } else {
        setPaymentDetails({
          paymentReference: result.payment_reference,
          amount: result.booking.price,
        });
        setPaymentOpen(true);
      }
      await refreshBookingsAndSlots();
    } catch (err) {
      if (isInvalidVoucherError(err)) {
        setVoucherError("Invalid voucher code. Please check and try again.");
      } else if (isSlotTakenError(err)) {
        setSlotToConfirm(null);
        setError(
          "That slot was just taken by someone else. The list has been refreshed.",
        );
        await loadSlots();
      } else {
        console.error("Error booking slot:", err);
        setError("Failed to book slot. Please try again.");
      }
    } finally {
      setBookingSlotKey(null);
    }
  };

  const handleConfirmReschedule = async () => {
    if (
      !reschedulingBooking ||
      !isValidVenueBooking(reschedulingBooking) ||
      isVenueBookingInPast(reschedulingBooking) ||
      !slotToReschedule ||
      isVenueBookingInPast(slotToReschedule)
    ) {
      return;
    }
    const booking = reschedulingBooking;
    const slot = slotToReschedule;
    const key = slotKey(slot);
    try {
      setBookingSlotKey(key);
      setError(null);
      await rescheduleVenueBooking(booking.id, {
        date: slot.date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        slot_length: slotLengthOf(slot),
      });
      setSlotToReschedule(null);
      setReschedulingBooking(null);
      setSuccessMessage("Booking rearranged successfully.");
      await refreshBookingsAndSlots();
    } catch (err) {
      if (isSlotTakenError(err)) {
        setSlotToReschedule(null);
        setError(
          "That slot was just taken by someone else. The list has been refreshed.",
        );
        await loadSlots();
      } else {
        console.error("Error rearranging booking:", err);
        setError("Failed to rearrange booking. Please try again.");
      }
    } finally {
      setBookingSlotKey(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!bookingToCancel || !isValidVenueBooking(bookingToCancel)) {
      return;
    }
    const cancelledId = bookingToCancel.id;
    try {
      setCancellingBookingId(cancelledId);
      setError(null);
      await cancelVenueBooking(cancelledId);
      setBookingToCancel(null);
      if (reschedulingBooking?.id === cancelledId) {
        setReschedulingBooking(null);
        setSlotToReschedule(null);
      }
      setSuccessMessage("Booking cancelled.");
      await refreshBookingsAndSlots();
    } catch (err) {
      console.error("Error cancelling booking:", err);
      setError("Failed to cancel booking. Please try again.");
    } finally {
      setCancellingBookingId(null);
    }
  };

  const handleRearrange = (booking: VenueBooking) => {
    if (!isValidVenueBooking(booking)) {
      return;
    }
    if (isVenueBookingInPast(booking)) {
      setError("Past bookings cannot be rearranged.");
      return;
    }
    setError(null);
    setSuccessMessage(null);
    setSlotToConfirm(null);
    setSlotToReschedule(null);
    setReschedulingBooking((current) =>
      current?.id === booking.id ? null : booking,
    );
  };

  const handleCancelBooking = (booking: VenueBooking) => {
    if (!isValidVenueBooking(booking)) {
      return;
    }
    setBookingToCancel(booking);
  };

  const selectedDateStr = selectedDate ? toDateString(selectedDate) : null;
  const selectedDaySlots = selectedDateStr
    ? filteredSlots
        .filter((slot) => slot.date === selectedDateStr)
        .sort((a, b) => {
          const byStart = a.start_time.localeCompare(b.start_time);
          if (byStart !== 0) return byStart;
          return slotLengthOf(a) - slotLengthOf(b);
        })
    : [];
  const availableCount = filteredSlots.filter(
    (s) => s.status === "available",
  ).length;
  const selectedAvailableCount = selectedDaySlots.filter(
    (s) => s.status === "available",
  ).length;
  const bookableDayCount = Object.values(slotSummaries).filter(
    (s) => s.hasAvailable,
  ).length;
  const filterLabel =
    SLOT_LENGTH_TABS.find((tab) => tab.value === slotLengthFilter)?.label ??
    "All";
  const hasAnySlots = displaySlots.length > 0;
  const hasFilteredSlots = filteredSlots.length > 0;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          display: "flex",
          alignItems: "center",
          fontWeight: 700,
          color: "#667eea",
        }}
      >
        <EventAvailable sx={{ mr: 1, fontSize: 20 }} />
        Venue Hire (DIY)
      </Typography>

      <MyVenueBookings
        clubId={clubId}
        refreshKey={bookingsRefreshKey}
        reschedulingBookingId={reschedulingBooking?.id ?? null}
        onCancel={handleCancelBooking}
        onRearrange={handleRearrange}
      />

      {isRearranging && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setReschedulingBooking(null);
                setSlotToReschedule(null);
              }}
            >
              Cancel rearrange
            </Button>
          }
        >
          Rearranging{" "}
          {formatSlotRange(
            reschedulingBooking.date,
            reschedulingBooking.start_time,
            reschedulingBooking.end_time,
          )}{" "}
          — choose another slot at{" "}
          {formatVenueBookingPrice(reschedulingBooking.price)}.
        </Alert>
      )}

      {venueHire.notes && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {venueHire.notes}
        </Alert>
      )}

      {!isRearranging && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {venueHirePaymentDeadlineNotice}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !hasAnySlots ? (
        <Typography variant="body2" color="text.secondary">
          {isRearranging
            ? `No other slots at ${formatVenueBookingPrice(reschedulingBooking.price)} are available right now. Try again later or cancel rearrange.`
            : `No DIY slots available at ${clubName} right now. Check back later.`}
        </Typography>
      ) : (
        <>
          <Tabs
            value={slotLengthFilter}
            onChange={(_, value: SlotLengthFilter) =>
              setSlotLengthFilter(value)
            }
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            {SLOT_LENGTH_TABS.map((tab) => (
              <Tab
                key={String(tab.value)}
                label={tab.label}
                value={tab.value}
                disabled={
                  isRearranging &&
                  tab.value !== "all" &&
                  tab.value !== getVenueBookingSlotLength(reschedulingBooking)
                }
              />
            ))}
          </Tabs>

          {!hasFilteredSlots ? (
            <Typography variant="body2" color="text.secondary">
              No {filterLabel === "All" ? "" : `${filterLabel} `}slots available
              right now. Try another session length.
            </Typography>
          ) : (
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={5} lg={4}>
                <VenueHireDateCalendar
                  value={selectedDate}
                  onChange={setSelectedDate}
                  highlightedDates={bookableHighlightDates}
                  minDate={dayjs()}
                  maxDate={rangeEnd}
                  shouldDisableDate={(date) =>
                    !slotSummaries[toDateString(date)]?.hasSlots
                  }
                  highlightColor="success"
                  legendLabel="Slots available"
                  legendCount={bookableDayCount}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 1,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  {availableCount} bookable slot{availableCount === 1 ? "" : "s"}{" "}
                  across {bookableDayCount} day{bookableDayCount === 1 ? "" : "s"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={7} lg={8}>
                {selectedDate && selectedDateStr ? (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      {formatDate(selectedDateStr)}
                    </Typography>

                    {selectedDaySlots.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No slots on this date.
                      </Typography>
                    ) : selectedAvailableCount === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {slotLengthFilter === "all"
                          ? "All slots on this date are booked or unavailable."
                          : `No available ${filterLabel} slots on this date.`}
                      </Typography>
                    ) : (
                      <Grid container spacing={1.5}>
                        {selectedDaySlots.map((slot) => {
                          const key = slotKey(slot);
                          const isAvailable = slot.status === "available";
                          const isBooking = bookingSlotKey === key;
                          const slotLength = slotLengthOf(slot);
                          return (
                            <Grid item xs={12} sm={6} key={key}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  opacity: isAvailable ? 1 : 0.65,
                                  borderColor:
                                    slot.status === "available"
                                      ? "success.light"
                                      : slot.status === "booked"
                                        ? "grey.300"
                                        : "warning.light",
                                }}
                              >
                                <Stack spacing={1}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {formatTime(slot.start_time)} –{" "}
                                    {formatTime(slot.end_time)}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    <Chip
                                      label={`${slotLength} min`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={
                                        slot.status === "available"
                                          ? `£${slot.price.toFixed(2)}`
                                          : slot.status === "booked"
                                            ? "Booked"
                                            : "Unavailable"
                                      }
                                      size="small"
                                      color={
                                        slot.status === "available"
                                          ? "success"
                                          : "default"
                                      }
                                    />
                                  </Stack>
                                  {isAvailable && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disabled={isBooking}
                                      onClick={() => handleBookClick(slot)}
                                      startIcon={
                                        isBooking ? (
                                          <CircularProgress
                                            size={16}
                                            color="inherit"
                                          />
                                        ) : undefined
                                      }
                                    >
                                      {isBooking
                                        ? isRearranging
                                          ? "Moving…"
                                          : "Booking…"
                                        : isRearranging
                                          ? "Move here"
                                          : "Book"}
                                    </Button>
                                  )}
                                </Stack>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a highlighted date to see available slots.
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </>
      )}

      <ConfirmVenueBookingDialog
        open={slotToConfirm != null}
        slot={slotToConfirm}
        clubName={clubName}
        confirming={bookingSlotKey != null}
        requiresName={requiresName}
        voucherError={voucherError}
        onClose={() => {
          setSlotToConfirm(null);
          setVoucherError(null);
        }}
        onConfirm={handleConfirmBooking}
      />

      <ConfirmVenueRescheduleDialog
        open={
          slotToReschedule != null &&
          reschedulingBooking != null &&
          isValidVenueBooking(reschedulingBooking) &&
          !isVenueBookingInPast(reschedulingBooking) &&
          !isVenueBookingInPast(slotToReschedule)
        }
        booking={reschedulingBooking}
        slot={slotToReschedule}
        clubName={clubName}
        confirming={bookingSlotKey != null}
        onClose={() => setSlotToReschedule(null)}
        onConfirm={handleConfirmReschedule}
      />

      <CancelVenueBookingDialog
        open={
          bookingToCancel != null && isValidVenueBooking(bookingToCancel)
        }
        booking={bookingToCancel}
        clubName={clubName}
        cancelling={cancellingBookingId != null}
        onClose={() => setBookingToCancel(null)}
        onConfirmCancel={handleConfirmCancel}
      />

      <BankTransferPaymentDialog
        open={paymentOpen}
        details={paymentDetails}
        showPaymentDeadlineNotice
        onClose={() => {
          setPaymentOpen(false);
          setPaymentDetails(null);
        }}
      />
    </Box>
  );
};

export default VenueHireBooking;
